import React, { useContext, useEffect, useState } from "react";

import { AccordionSection } from "components/accordion";
import DuplicateCheck from "components/duplicate-check";
import DualCodingAttributeTable from "../attributes/dual-coding-attribute-table";
import AddAttributeSearch from "./components/add-attribute-search";
import HeaderUpc from "../h1upc";
import NoteLauncher from "../notes";
import QANav from "../task-nav/qa-nav";
import CodingQAIndex from "./coding-qa";
import TaskMessages from "../notification-bar/task-messages";
import TaskNav from "../task-nav/task-nav";

import { TaskContext } from "../../contexts/task";
import { SourcePage, CodingSource } from "shared/taskTypeMap";
import { useAttributesValidity } from "../../shared/hooks";
import { validateTask } from "../../shared/taskState";
import { useAlert } from "hooks/useAlert";
import { SettingsContext } from "contexts/settings";
import { SidePanel } from "../side-panel/side-panel";
import { ToggleSwitch } from "../form-inputs/custom/toggle-switch";
import { useViewOnlyPermissions } from "shared/hooks/use-view-only-permissions";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { BRAND_VERIFIED_ATTR_GROUPS } from "./lib/constants";
import { useAttributesByRole } from "shared/hooks/api/use-attributes-by-role";
import { useReferences } from "components/attributes/references/hooks/useReferences";

import { ReferencesProvider } from "components/attributes/references/contexts/references-provider";

import "./coding.scss";
import { useShipmentProductFeatures } from "../../shared/hooks/use-shipment-product-features";
import { cesFeatureGuids } from "../../contexts/settings/lib/constants";
import NotificationBar from "../notification-bar";
import { ReferenceImages } from "../../shared/components/reference-images/reference-images";

const CodingIndex = () => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const isQA = matchPath("/coding-qa/:id/:wip/:group?", pathname);
    const { toggleAlert } = useAlert();
    const { settings } = useContext(SettingsContext);
    const { task } = useContext(TaskContext);
    const codingWipKey = task.context.codingWipKey;
    const [showValidation, setShowValidation] = useState(false);
    const attributesValidity = useAttributesValidity();
    const [controlsHidden, setHideControls] = useState(false);
    const [showReferences, setShowReferences] = useState(true);
    const [openSection, setOpenSection] = useState(true);
    const [hasRedo, setHasRedo] = useState(false);
    const [isRefreshing, setRefresh] = useState(false);
    const [definitions, setDefinitions] = useState({});
    const { references } = useReferences();

    const { featureName: costcoFeatureName } = useShipmentProductFeatures(cesFeatureGuids.COSTCOSPSUP, task.context.shipmentProductKey);

    const isVOCompleteTask = useViewOnlyPermissions("CompleteTask", CodingSource[task.taskType]);
    const isVONotes = useViewOnlyPermissions("CreateNote", task.sourcePage);
    const isReadOnlyAdd = useViewOnlyPermissions("AddAttribute", task.sourcePage);
    const isReadOnlyEdit = useViewOnlyPermissions("EditAttribute", task.sourcePage);
    const isReadOnlyDelete = useViewOnlyPermissions("DeleteAttribute", task.sourcePage);
    const isReadOnlyReject = useViewOnlyPermissions("RejectTask", task.sourcePage);

    const isBrandVerified = task.context.workflowTypeId === 2;

    const {
        data: attributeRoles,
        isLoading: isAttributeRolesLoading,
        isSuccess: isAttributeRolesFetched,
        refetch: refetchAttributeRoles,
    } = useAttributesByRole(
        task.key,
        task.taskType,
        codingWipKey,
        [...references.activeProductKeys],
        isBrandVerified,
        BRAND_VERIFIED_ATTR_GROUPS[task.taskType]
    );

    useEffect(() => {
        if (attributeRoles && isAttributeRolesFetched) {
            return setDefinitions(attributeRoles);
        }
    }, [attributeRoles, isAttributeRolesFetched]);

    useEffect(() => {
        if (settings.globalNotifications.messages.length) {
            toggleAlert({ group: isQA ? "CodingQA" : "Coding" });
        }
    }, [settings.globalNotifications.messages.length]);

    const validate = async (override = false) => {
        if (!attributesValidity.isValid) {
            setShowValidation(true);
            window.scrollTo({
                top: 0,
                behavior: "smooth",
            });

            return false;
        }

        return await validateTask(task.key, override, { passthrough: true, displayError: true });
    };

    const onComplete = () => {
        const backUrl = isQA ? "/task-list/codingqa" : "/task-list/coding";
        navigate(backUrl);
    };

    const handleHideControls = (isHidden) => setHideControls(isHidden);

    const coderRole = task.description.split(" ")[0];
    const coderLabel = coderRole === "Std" ? "Standard" : coderRole;
    const addSideScroll = isQA ? "side-scroll" : "";

    return (
        <div className="flex">
            <SidePanel />
            <div
                id="coding"
                className={`container flex-1 ${addSideScroll}`}
            >
                <div>
                    <TaskNav
                        taskKey={task.key}
                        hasIMDropboxContent={task.hasIMDropboxContent}
                        isSampleProduct={task.isSampleUpc}
                        fixAfter={250}
                    >
                        <div className="button-group">
                            <HeaderUpc isBrandVerified={isBrandVerified} />
                            {!controlsHidden && (
                                <div className="button-group -right -margin-right">
                                    {isQA && (
                                        <div className="flex justify-content-end m-r-16">
                                            <ToggleSwitch
                                                variant="small"
                                                name="sendToGdsn"
                                                value={showReferences}
                                                updateOnChange={(name, value) => setShowReferences(value)}
                                            />
                                            <div className="m-l-4">View Reference Products</div>
                                        </div>
                                    )}
                                    {!task.isCompleted && !isReadOnlyAdd && !hasRedo && (
                                        <AddAttributeSearch
                                            cssClass="-right"
                                            wipKey={codingWipKey}
                                            taskType={isQA ? "codingqa.standard" : "coding.standard"}
                                            isReadOnly={isVOCompleteTask}
                                            taskKey={task.key}
                                            isSuccess={!isRefreshing}
                                            isBrandVerified={isBrandVerified}
                                        />
                                    )}
                                </div>
                            )}
                            {task.context.matchedProductTaskContextKey && !controlsHidden && (
                                <DuplicateCheck matchType={task.context.productMatchType} />
                            )}
                            <NoteLauncher
                                settings={{
                                    shipmentKey: task.context.shipmentKey,
                                    shipmentProductKey: task.context.shipmentProductKey,
                                }}
                                clientCode={task.context.cxHClientCode}
                                binTrackingNumber={[task.context.binTrackingNumber]}
                                noteType="Product"
                                taskType={isQA ? "codingqa.standard" : "coding.standard"}
                                sourcePage={SourcePage[task.taskType]}
                                isReadOnly={isVONotes}
                            />

                            {!task.isCompleted && !controlsHidden && (
                                <QANav
                                    taskId={task.key}
                                    onComplete={onComplete}
                                    validate={validate}
                                    isQA={isQA}
                                    hideReject={true}
                                    taskType="coding.standard"
                                    sourcePage={SourcePage[task.taskType]}
                                    codingSource={CodingSource[task.taskType]}
                                    isReadOnly={isVOCompleteTask}
                                    isReadOnlyReject={isReadOnlyReject}
                                    hasRedo={hasRedo}
                                />
                            )}
                        </div>
                    </TaskNav>
                    {costcoFeatureName && (
                        <div className="m-l-30">
                            <NotificationBar
                                className="m-l-32"
                                variant="small"
                                type="info"
                            >
                                {costcoFeatureName}
                            </NotificationBar>
                        </div>
                    )}
                    {isBrandVerified && (
                        <div className="m-l-30">
                            <NotificationBar
                                className="m-l-32"
                                variant="small"
                                type="info"
                            >
                                <span className="txt-bold">Brand Verified</span>
                            </NotificationBar>
                        </div>
                    )}
                    <TaskMessages message={task.taskMessage} />
                    {!isQA && (
                        <div className="accordion">
                            <AccordionSection
                                label={`${coderLabel} Attributes`}
                                isOpen={openSection}
                                arrowOnly={false}
                                onClick={() => setOpenSection(!openSection)}
                            >
                                <ReferencesProvider
                                    codingWipKey={codingWipKey}
                                    task={task}
                                    onHideControls={handleHideControls}
                                >
                                    <DualCodingAttributeTable
                                        showReferences={showReferences}
                                        showValidation={showValidation}
                                        isQa={isQA}
                                        taskType={"coding.standard"}
                                        isReadOnlyAdd={isReadOnlyAdd}
                                        isReadOnlyEdit={isReadOnlyEdit}
                                        isReadOnlyDelete={isReadOnlyDelete}
                                        isRefreshing={isRefreshing}
                                        setRefresh={setRefresh}
                                        isBrandVerified={isBrandVerified}
                                        roleDefinitions={definitions}
                                        refetchAttributeRoles={refetchAttributeRoles}
                                        isAttributeRolesLoading={isAttributeRolesLoading}
                                    />
                                </ReferencesProvider>
                            </AccordionSection>
                        </div>
                    )}
                    {isQA && (
                        <ReferencesProvider
                            codingWipKey={codingWipKey}
                            task={task}
                            onHideControls={handleHideControls}
                        >
                            <CodingQAIndex
                                codingWipKey={codingWipKey}
                                showReferences={showReferences}
                                showValidation={showValidation}
                                isQa={isQA}
                                taskKey={task.key}
                                hasRedo={hasRedo}
                                setHasRedo={setHasRedo}
                                isReadOnlyAdd={isReadOnlyAdd}
                                isReadOnlyEdit={isReadOnlyEdit}
                                isReadOnlyDelete={isReadOnlyDelete}
                                isRefreshing={isRefreshing}
                                setRefresh={setRefresh}
                                isBrandVerified={isBrandVerified}
                            />
                        </ReferencesProvider>
                    )}
                    <ReferenceImages
                        photographyWipKey={task.context.photographyWipKey}
                        shipmentKey={task.context.shipmentKey}
                        shipmentProductKey={task.context.shipmentProductKey}
                    />
                </div>
            </div>
        </div>
    );
};

export default CodingIndex;
